import React, { useRef, useEffect, useState, useMemo, useCallback, useId } from 'react';
import { formatScore } from '../services/coordinateUtils';
import './WinrateChart.css';

export interface WinratePoint {
  moveNum: number;   // 1-based
  winprob: number;   // 0–1, Black's win probability
  score?: number;
  move?: string;     // GTP/SGF notation for tooltip
  engine?: string;
}

interface Props {
  points: WinratePoint[];
  currentMove?: number;            // 1-based, highlight with vertical line
  onMoveClick?: (moveNum: number) => void;
  height?: number;
}

const PAD = { top: 18, right: 16, bottom: 26, left: 38 };

export default function WinrateChart({ points, currentMove, onMoveClick, height = 150 }: Props) {
  const uid        = useId().replace(/:/g, '');
  const containerRef = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(600);
  const [hoverIdx, setHoverIdx] = useState<number | null>(null);

  // Responsive width
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const ro = new ResizeObserver(entries => setWidth(entries[0].contentRect.width));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const innerW = width - PAD.left - PAD.right;
  const innerH = height - PAD.top - PAD.bottom;

  const toX = useCallback((idx: number) =>
    PAD.left + (points.length <= 1 ? innerW / 2 : (idx / (points.length - 1)) * innerW),
    [points.length, innerW]);

  const toY = useCallback((wp: number) =>
    PAD.top + (1 - wp) * innerH,
    [innerH]);

  const midY = toY(0.5);

  const linePath = useMemo(() => {
    if (points.length < 2) return '';
    return points.map((p, i) =>
      `${i === 0 ? 'M' : 'L'}${toX(i).toFixed(1)},${toY(p.winprob).toFixed(1)}`
    ).join(' ');
  }, [points, toX, toY]);

  // Area closed to midline, split-colored by clip
  const areaPath = useMemo(() => {
    if (points.length < 2) return '';
    const line = points.map((p, i) =>
      `${i === 0 ? 'M' : 'L'}${toX(i).toFixed(1)},${toY(p.winprob).toFixed(1)}`
    ).join(' ');
    return `${line} L${toX(points.length - 1).toFixed(1)},${midY.toFixed(1)} L${toX(0).toFixed(1)},${midY.toFixed(1)} Z`;
  }, [points, toX, toY, midY]);

  // X-axis ticks: at most ~8
  const xTicks = useMemo(() => {
    const n = points.length;
    if (n === 0) return [];
    const step = Math.max(1, Math.ceil(n / 8));
    const ticks: number[] = [];
    for (let i = 0; i < n; i += step) ticks.push(i);
    if (ticks[ticks.length - 1] !== n - 1) ticks.push(n - 1);
    return ticks;
  }, [points.length]);

  const currentIdx = currentMove != null
    ? points.findIndex(p => p.moveNum === currentMove)
    : -1;

  const handleMouseMove = useCallback((e: React.MouseEvent<SVGSVGElement>) => {
    if (points.length === 0) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const mx   = e.clientX - rect.left - PAD.left;
    const idx  = Math.round((mx / innerW) * (points.length - 1));
    setHoverIdx(Math.max(0, Math.min(points.length - 1, idx)));
  }, [points.length, innerW]);

  const hoverPt = hoverIdx !== null ? points[hoverIdx] : null;

  // Tooltip position — keep inside SVG
  const tooltip = useMemo(() => {
    if (!hoverPt || hoverIdx === null) return null;
    const tx   = toX(hoverIdx);
    const ty   = toY(hoverPt.winprob);
    const tipW = 130, tipH = hoverPt.score != null ? 58 : 44;
    const tipX = Math.min(Math.max(tx - tipW / 2, PAD.left), width - tipW - 4);
    const tipY = ty < PAD.top + innerH / 2 ? ty + 12 : ty - tipH - 8;
    return { tx, ty, tipX, tipY, tipW, tipH };
  }, [hoverPt, hoverIdx, toX, toY, width, innerH]);

  return (
    <div ref={containerRef} className="wrchart-wrap">
      <svg
        width={width}
        height={height}
        style={{ display: 'block', cursor: onMoveClick ? 'pointer' : 'default' }}
        onMouseMove={handleMouseMove}
        onMouseLeave={() => setHoverIdx(null)}
        onClick={() => {
          if (hoverIdx !== null && onMoveClick) onMoveClick(points[hoverIdx].moveNum);
        }}
      >
        <defs>
          <clipPath id={`ct-${uid}`}>
            <rect x={PAD.left} y={PAD.top} width={innerW} height={Math.max(0, midY - PAD.top)} />
          </clipPath>
          <clipPath id={`cb-${uid}`}>
            <rect x={PAD.left} y={midY} width={innerW} height={Math.max(0, PAD.top + innerH - midY)} />
          </clipPath>
        </defs>

        {/* Grid lines */}
        {([0.75, 0.5, 0.25] as const).map(wp => (
          <line key={wp}
            x1={PAD.left} y1={toY(wp)} x2={PAD.left + innerW} y2={toY(wp)}
            stroke={wp === 0.5 ? '#aaa' : '#e5e7eb'}
            strokeWidth={wp === 0.5 ? 1.5 : 1}
            strokeDasharray={wp === 0.5 ? '4 3' : undefined}
          />
        ))}

        {/* Y-axis labels */}
        <text x={PAD.left - 6} y={toY(1.0)  + 4}  textAnchor="end" fontSize={10} fill="#aaa">100%</text>
        <text x={PAD.left - 6} y={toY(0.5)  + 4}  textAnchor="end" fontSize={10} fill="#aaa">50%</text>
        <text x={PAD.left - 6} y={toY(0.0)  + 4}  textAnchor="end" fontSize={10} fill="#aaa">0%</text>

        {/* Area fills — blue above 50%, orange below */}
        {areaPath && <>
          <path d={areaPath} fill="rgba(59,130,246,0.15)" clipPath={`url(#ct-${uid})`} />
          <path d={areaPath} fill="rgba(245,158,11,0.15)" clipPath={`url(#cb-${uid})`} />
        </>}

        {/* Win line */}
        {linePath && <path d={linePath} fill="none" stroke="#3b82f6" strokeWidth={2} strokeLinejoin="round" strokeLinecap="round" />}

        {/* Empty state label */}
        {points.length === 0 && (
          <text x={PAD.left + innerW / 2} y={PAD.top + innerH / 2 + 4}
            textAnchor="middle" fontSize={12} fill="#ccc">
            Waiting for moves…
          </text>
        )}

        {/* Current move vertical indicator */}
        {currentIdx >= 0 && (
          <line
            x1={toX(currentIdx)} y1={PAD.top}
            x2={toX(currentIdx)} y2={PAD.top + innerH}
            stroke="#4a90e2" strokeWidth={1.5} strokeDasharray="3 2"
          />
        )}
        {currentIdx >= 0 && (
          <circle cx={toX(currentIdx)} cy={toY(points[currentIdx].winprob)}
            r={5} fill="#4a90e2" stroke="#fff" strokeWidth={2} />
        )}

        {/* Hover dot */}
        {hoverIdx !== null && hoverIdx !== currentIdx && (
          <circle cx={toX(hoverIdx)} cy={toY(hoverPt!.winprob)}
            r={4} fill="#fff" stroke="#4a90e2" strokeWidth={2} />
        )}

        {/* X-axis ticks */}
        {xTicks.map(i => (
          <text key={i} x={toX(i)} y={PAD.top + innerH + 16}
            textAnchor="middle" fontSize={9} fill="#bbb">
            {points[i].moveNum}
          </text>
        ))}

        {/* Tooltip */}
        {tooltip && hoverPt && (
          <g>
            <rect x={tooltip.tipX} y={tooltip.tipY}
              width={tooltip.tipW} height={tooltip.tipH}
              rx={5} fill="rgba(20,20,30,0.90)" />
            <text x={tooltip.tipX + 9} y={tooltip.tipY + 15}
              fontSize={11} fill="#fff" fontWeight={600}>
              Move {hoverPt.moveNum}{hoverPt.move ? ` · ${hoverPt.move}` : ''}
            </text>
            <text x={tooltip.tipX + 9} y={tooltip.tipY + 30}
              fontSize={11} fill="#93c5fd">
              Black: {(hoverPt.winprob * 100).toFixed(1)}%
              {'  '}White: {((1 - hoverPt.winprob) * 100).toFixed(1)}%
            </text>
            {hoverPt.score != null && (
              <text x={tooltip.tipX + 9} y={tooltip.tipY + 45}
                fontSize={11} fill="#fcd34d">
                Score: {formatScore(hoverPt.score)}
                {hoverPt.engine ? `  · ${hoverPt.engine}` : ''}
              </text>
            )}
          </g>
        )}
      </svg>
    </div>
  );
}
